import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Row, Col, notification, Form, Input } from 'antd';
import { ThemeModal } from '../../pages/Styles/Theme';
import {
  changePasswordApi,
  changePasswordReset,
} from '../../store/actions/userAction';
import styled from 'styled-components';

const StyledForm = styled(Form)`
  .ant-form-item-label > label {
    font-size: 14px;
    font-weight: 500;
    color: ${({ theme }) => theme.settings.primary};
  }
  .ant-form-item {
    margin-bottom: 16px;
  }
`;

const StyledPasswordInput = styled(Input.Password)`
  border-radius: 8px;
  height: 44px;
  font-size: 14px;
  &:focus, &:hover {
    border-color: ${({ theme }) => theme.settings.primary};
  }
`;

const ChangePasswordModal = (props) => {
  const {
    open,
    onClose,
    changePassword,
    changePasswordReset,
    changePasswordState,
  } = props;

  const [form] = Form.useForm();

  // Handle password change response
  useEffect(() => {
    if (changePasswordState.apiState === 'success') {
      notification.success({
        message: 'Success',
        description: changePasswordState.message || 'Password changed successfully',
      });
      form.resetFields();
      changePasswordReset();
      onClose();
    } else if (changePasswordState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: changePasswordState.message || 'Failed to change password',
      });
      changePasswordReset();
    }
  }, [changePasswordState, form, changePasswordReset, onClose]);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      form.resetFields();
    }
  }, [open, form]);

  const handleSubmit = (values) => {
    changePassword({
      password: values.password,
      new_password: values.new_password,
      confirm_password: values.confirm_password,
    });
  };

  const handleCancel = () => {
    form.resetFields();
    changePasswordReset();
    onClose();
  };

  return (
    <ThemeModal
      title="Change Password"
      open={open}
      onCancel={handleCancel}
      onOk={() => form.submit()}
      okText="Save"
      cancelText="Cancel"
      confirmLoading={changePasswordState.apiState === 'loading'}
      width={450}
      destroyOnClose
      maskClosable={false}
    >
      <StyledForm
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
      >
        <Row gutter={[16, 0]}>
          <Col span={24}>
            <Form.Item
              label="Current Password"
              name="password"
              rules={[
                {
                  required: true,
                  message: 'Please enter your current password',
                },
              ]}
            >
              <StyledPasswordInput placeholder="Enter your current password" />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              label="New Password"
              name="new_password"
              rules={[
                {
                  required: true,
                  message: 'Please enter your new password',
                },
                {
                  min: 8,
                  message: 'Password must be at least 8 characters long',
                },
                {
                  max: 20,
                  message: 'Password must not exceed 20 characters',
                },
                {
                  pattern: /^[A-Za-z\d@$!%*?&]*$/,
                  message: 'Only letters, numbers and @$!%*?& special characters are allowed',
                },
                {
                  pattern: /[A-Z]/,
                  message: 'Password must contain at least one uppercase letter',
                },
                {
                  pattern: /[a-z]/,
                  message: 'Password must contain at least one lowercase letter',
                },
                {
                  pattern: /\d/,
                  message: 'Password must contain at least one number',
                },
                {
                  pattern: /[@$!%*?&]/,
                  message: 'Password must contain at least one special character (@$!%*?&)',
                },
              ]}
            >
              <StyledPasswordInput placeholder="Enter your new password" />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              label="Confirm New Password"
              name="confirm_password"
              dependencies={['new_password']}
              hasFeedback
              rules={[
                {
                  required: true,
                  message: 'Please confirm your new password',
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('new_password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error('The two passwords that you entered do not match!')
                    );
                  },
                }),
              ]}
            >
              <StyledPasswordInput placeholder="Confirm your new password" />
            </Form.Item>
          </Col>
        </Row>
      </StyledForm>
    </ThemeModal>
  );
};

const mapStateToProps = (state) => ({
  changePasswordState: state.user.changePasswordState,
});

const mapDispatchToProps = (dispatch) => ({
  changePassword: (data) => dispatch(changePasswordApi(data)),
  changePasswordReset: () => dispatch(changePasswordReset()),
});

export default connect(mapStateToProps, mapDispatchToProps)(ChangePasswordModal);
